import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Fingerprint, ShieldCheck, Trash2, KeyRound } from 'lucide-react';
import Layout from '../components/Layout.js';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { authClient } from '../lib/authClient.js';

interface PasskeyRow {
  id: string;
  name?: string | null;
  createdAt?: string | Date | null;
}

const card = 'rounded-xl bg-panel border border-line p-6 space-y-4';
const inputCls =
  'block w-full rounded-lg bg-panel border border-line px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition';
const primaryBtn =
  'rounded-full bg-accent-600 px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity';
const ghostBtn =
  'rounded-full border border-line px-5 py-2 text-sm font-medium text-ink hover:bg-raised disabled:opacity-50 transition-colors';

export default function Security() {
  const { t } = useAppTranslation();
  const { data: session, refetch } = authClient.useSession();
  const twoFactorEnabled = Boolean((session?.user as { twoFactorEnabled?: boolean } | undefined)?.twoFactorEnabled);

  const [password, setPassword] = useState('');
  const [setup, setSetup] = useState<{ qr: string; secret: string; backupCodes: string[] } | null>(null);
  const [code, setCode] = useState('');
  const [newCodes, setNewCodes] = useState<string[] | null>(null);
  const [passkeys, setPasskeys] = useState<PasskeyRow[]>([]);
  const [passkeyName, setPasskeyName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const generic = t('common.errors.genericRetry', 'Ein Fehler ist aufgetreten. Bitte erneut versuchen.');

  async function loadPasskeys() {
    const res = await authClient.passkey.listUserPasskeys();
    setPasskeys((res.data as PasskeyRow[] | null) ?? []);
  }

  useEffect(() => {
    void loadPasskeys();
  }, []);

  async function run(fn: () => Promise<void>) {
    setError('');
    setMessage('');
    setBusy(true);
    try {
      await fn();
    } catch {
      setError(generic);
    } finally {
      setBusy(false);
    }
  }

  const startSetup = () =>
    run(async () => {
      const { data, error: err } = await authClient.twoFactor.enable({ password });
      if (err || !data || data.method !== 'totp') {
        setError(t('security.errors.password', 'Das Passwort ist nicht korrekt.'));
        return;
      }
      const secret = new URL(data.totpURI).searchParams.get('secret') ?? '';
      setSetup({ qr: await QRCode.toDataURL(data.totpURI, { margin: 1, width: 192 }), secret, backupCodes: data.backupCodes });
      setPassword('');
    });

  const confirmSetup = () =>
    run(async () => {
      const { error: err } = await authClient.twoFactor.verifyTotp({ code: code.replace(/\s/g, '') });
      if (err) {
        setError(t('security.errors.code', 'Der Code ist ungültig.'));
        return;
      }
      setNewCodes(setup?.backupCodes ?? null);
      setSetup(null);
      setCode('');
      setMessage(t('security.twoFactor.enabled', 'Zwei-Faktor-Authentifizierung ist aktiv.'));
      await refetch();
    });

  const disable2fa = () =>
    run(async () => {
      const { error: err } = await authClient.twoFactor.disable({ password });
      if (err) {
        setError(t('security.errors.password', 'Das Passwort ist nicht korrekt.'));
        return;
      }
      setPassword('');
      setNewCodes(null);
      setMessage(t('security.twoFactor.disabled', 'Zwei-Faktor-Authentifizierung ist ausgeschaltet.'));
      await refetch();
    });

  const regenerateCodes = () =>
    run(async () => {
      const { data, error: err } = await authClient.twoFactor.generateBackupCodes({ password });
      if (err || !data) {
        setError(t('security.errors.password', 'Das Passwort ist nicht korrekt.'));
        return;
      }
      setPassword('');
      setNewCodes(data.backupCodes);
    });

  const addPasskey = () =>
    run(async () => {
      const res = await authClient.passkey.addPasskey(passkeyName ? { name: passkeyName } : {});
      if (res?.error) {
        setError(t('security.errors.passkey', 'Der Passkey konnte nicht angelegt werden.'));
        return;
      }
      setPasskeyName('');
      await loadPasskeys();
    });

  const removePasskey = (id: string) =>
    run(async () => {
      await authClient.passkey.deletePasskey({ id });
      await loadPasskeys();
    });

  return (
    <Layout title={t('nav.security', 'Sicherheit')}>
      <div className="max-w-xl space-y-6">
        {error && (
          <div className="rounded-lg bg-danger-100 dark:bg-danger-500/10 border border-danger-500/20 px-4 py-2.5 text-sm text-danger-400">
            {error}
          </div>
        )}
        {message && (
          <div className="rounded-lg bg-raised border border-line px-4 py-2.5 text-sm text-ink-muted">{message}</div>
        )}

        {/* Passkeys */}
        <section className={card}>
          <div className="flex items-center gap-2">
            <Fingerprint className="h-4 w-4 text-accent-400" />
            <h2 className="text-sm font-semibold text-ink">{t('security.passkeys.title', 'Passkeys')}</h2>
          </div>
          <p className="text-xs text-ink-faint">
            {t('security.passkeys.hint', 'Anmelden mit Fingerabdruck, Gesichtserkennung oder Sicherheitsschlüssel – ohne Passwort und nicht abfangbar.')}
          </p>
          {passkeys.length > 0 && (
            <ul className="divide-y divide-line rounded-lg border border-line">
              {passkeys.map((p) => (
                <li key={p.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="text-ink">{p.name || t('security.passkeys.unnamed', 'Passkey')}</span>
                  <button
                    type="button"
                    aria-label={t('common.actions.delete', 'Löschen')}
                    onClick={() => removePasskey(p.id)}
                    disabled={busy}
                    className="text-ink-faint hover:text-danger-400 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2">
            <input
              className={inputCls}
              value={passkeyName}
              onChange={(e) => setPasskeyName(e.target.value)}
              placeholder={t('security.passkeys.namePlaceholder', 'Name, z. B. MacBook')}
            />
            <button type="button" onClick={addPasskey} disabled={busy} className={primaryBtn}>
              {t('security.passkeys.add', 'Hinzufügen')}
            </button>
          </div>
        </section>

        {/* Two-factor */}
        <section className={card}>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-accent-400" />
            <h2 className="text-sm font-semibold text-ink">
              {t('security.twoFactor.title', 'Zwei-Faktor-Authentifizierung (Authenticator-App)')}
            </h2>
          </div>

          {setup ? (
            <div className="space-y-3">
              <p className="text-xs text-ink-faint">
                {t('security.twoFactor.scan', 'Scanne den QR-Code mit deiner Authenticator-App und gib den 6-stelligen Code ein.')}
              </p>
              <img src={setup.qr} alt="" className="h-48 w-48 rounded-lg bg-white p-1" />
              <p className="text-xs text-ink-faint break-all">
                {t('security.twoFactor.manual', 'Oder manuell')}: <span className="font-mono text-ink-muted">{setup.secret}</span>
              </p>
              <div className="flex gap-2">
                <input
                  className={inputCls}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                />
                <button type="button" onClick={confirmSetup} disabled={busy || !code} className={primaryBtn}>
                  {t('security.twoFactor.confirm', 'Bestätigen')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-ink-faint">
                {twoFactorEnabled
                  ? t('security.twoFactor.activeHint', 'Aktiv. Beim Anmelden mit Passwort wird zusätzlich ein Code abgefragt. Anmelde-Links per E-Mail sind für dieses Konto abgeschaltet.')
                  : t('security.twoFactor.inactiveHint', 'Schütze dein Konto zusätzlich mit einem Code aus einer Authenticator-App.')}
              </p>
              <input
                type="password"
                className={inputCls}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('security.currentPassword', 'Aktuelles Passwort')}
                autoComplete="current-password"
              />
              <div className="flex flex-wrap gap-2">
                {twoFactorEnabled ? (
                  <>
                    <button type="button" onClick={regenerateCodes} disabled={busy || !password} className={ghostBtn}>
                      {t('security.twoFactor.newCodes', 'Neue Backup-Codes')}
                    </button>
                    <button type="button" onClick={disable2fa} disabled={busy || !password} className={ghostBtn}>
                      {t('security.twoFactor.disable', 'Ausschalten')}
                    </button>
                  </>
                ) : (
                  <button type="button" onClick={startSetup} disabled={busy || !password} className={primaryBtn}>
                    {t('security.twoFactor.enable', 'Einrichten')}
                  </button>
                )}
              </div>
            </div>
          )}

          {newCodes && (
            <div className="space-y-2 rounded-lg border border-line bg-raised p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-ink">
                <KeyRound className="h-4 w-4" />
                {t('security.backup.title', 'Backup-Codes')}
              </div>
              <p className="text-xs text-ink-faint">
                {t('security.backup.hint', 'Jeder Code funktioniert einmal. Bewahre sie sicher auf – sie werden nicht noch einmal angezeigt.')}
              </p>
              <div className="grid grid-cols-2 gap-1 font-mono text-sm text-ink">
                {newCodes.map((c) => (
                  <span key={c}>{c}</span>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
